import React from 'react';
import { useGetGlobalAttributes, useGetXstyledProps, useMemoMergePropsWithDefault } from '@elliemae/ds-props-helpers';
import { type TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import type { DSButtonT } from '../react-desc-prop-types.js';
import { defaultProps, DSButtonPropTypes } from '../react-desc-prop-types.js';
import { useValidateProps } from './useValidateProps.js';

export interface ButtonV2Configuration {
  propsWithDefault: DSButtonT.InternalProps;
  globalProps: ReturnType<typeof useGetGlobalAttributes>;
  xstyledProps: ReturnType<typeof useGetXstyledProps>;
  handleOnKeyDown: React.KeyboardEventHandler<HTMLButtonElement>;
  tabIndex?: TypescriptHelpersT.WCAGTabIndex;
  handleOnClick: (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => void;
  handleOnMouseDown: React.MouseEventHandler<HTMLButtonElement>;
}

export const useButton = (props: DSButtonT.Props): ButtonV2Configuration => {
  // =============================================================================
  // MERGE WITH DEFAULT AND VALIDATE PROPS
  // =============================================================================
  const propsWithDefault = useMemoMergePropsWithDefault<DSButtonT.InternalProps>(props, defaultProps);
  useValidateProps(props, DSButtonPropTypes);
  // =============================================================================
  // GLOBAL ATTRIBUTES & XSTYLED PROPS
  // =============================================================================
  const globalProps = useGetGlobalAttributes<DSButtonT.InternalProps, HTMLButtonElement>(propsWithDefault);
  const xstyledProps = useGetXstyledProps(propsWithDefault);
  const { disabled } = globalProps;
  // =============================================================================
  // AD HOC PER COMPONENT LOGIC
  // =============================================================================
  const { onKeyDown, onClick, onMouseDown, applyAriaDisabled, readOnly, type } = propsWithDefault;

  // html allows any caps combination, we don't want to re-render on case change and our checks to care about case-sensitivity
  const standardizedType = String(type).toLowerCase();

  const handleOnKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = React.useCallback(
    (e) => {
      const { key } = e;
      if (['Space', ' ', 'Enter'].includes(key) && (disabled || applyAriaDisabled || readOnly)) {
        e.preventDefault();
        return;
      }

      onKeyDown?.(e);
      if (standardizedType !== 'submit' && ['Space', ' ', 'Enter'].includes(key)) {
        e.preventDefault();
        onClick(e);
      }
    },
    [disabled, applyAriaDisabled, readOnly, onKeyDown, standardizedType, onClick],
  );

  const handleOnClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled || applyAriaDisabled || readOnly) {
        return;
      }

      onClick?.(e);
    },
    [disabled, applyAriaDisabled, readOnly, onClick],
  );

  const handleOnMouseDown: React.MouseEventHandler<HTMLButtonElement> = React.useCallback(
    (e) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      onMouseDown?.(e);
    },
    [disabled, onMouseDown],
  );

  const tabIndex = globalProps.disabled ? -1 : globalProps.tabIndex;

  return React.useMemo(
    () => ({
      propsWithDefault,
      globalProps,
      xstyledProps,
      handleOnKeyDown,
      handleOnClick,
      tabIndex,
      handleOnMouseDown,
    }),
    [propsWithDefault, globalProps, xstyledProps, handleOnKeyDown, handleOnClick, tabIndex, handleOnMouseDown],
  );
};
